import React from 'react';
import { Input } from '@roger/ui-components';

interface InstructionDisplayProps {
  instruction: string;
  className?: string;
}

/**
 * Component to display instruction text with formatted klabel and ktext tags
 * klabel tags are displayed as regular text
 * ktext tags are displayed as read-only input boxes
 */
const InstructionDisplay: React.FC<InstructionDisplayProps> = ({
  instruction,
  className = '',
}) => {
  if (!instruction) {
    return null;
  }

  // Parse the instruction string and convert klabel/ktext tags to React elements
  const parseInstruction = (text: string) => {
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;
    let key = 0;

    // Regular expression to match klabel and ktext tags
    const regex = /<(klabel|ktext)>(.*?)<\/\1>/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the tag
      if (match.index > currentIndex) {
        const textBefore = text.substring(currentIndex, match.index).trim();
        if (textBefore) {
          parts.push(
            <span key={key++} className='mr-1'>
              {textBefore}
            </span>
          );
        }
      }

      // Add the tagged content
      const tagType = match[1];
      const content = match[2];

      if (tagType === 'klabel') {
        // Regular text for klabel
        parts.push(
          <span key={key++} className='mr-1'>
            {content}
          </span>
        );
      } else if (tagType === 'ktext') {
        // Read-only input box for ktext (user-fillable fields)
        const width = Math.max(content.length * 8 + 20, 80);
        parts.push(
          <Input
            key={key++}
            placeholder={content}
            readOnly
            className='inline-block mx-1 h-6 px-2 text-sm bg-gray-100 border-gray-300 cursor-not-allowed'
            style={{
              width: `${width}px`,
              verticalAlign: 'middle',
            }}
          />
        );
      }

      currentIndex = match.index + match[0].length;
    }

    // Add remaining text after last tag
    if (currentIndex < text.length) {
      const remaining = text.substring(currentIndex).trim();
      if (remaining) {
        parts.push(
          <span key={key++} className='mr-1'>
            {remaining}
          </span>
        );
      }
    }

    return parts;
  };

  return (
    <div
      className={`text-sm flex flex-wrap items-center gap-1 leading-relaxed ${className}`}
    >
      {parseInstruction(instruction)}
    </div>
  );
};

export default InstructionDisplay;
